<template>
    <div class="bg-white py-16 mt-16">
        <div class="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <!-- Timeline Container -->
            <div class="relative pl-10 md:pl-0">
                <!-- Vertical Line: left on mobile, center on desktop -->
                <div
                    class="absolute left-4 md:left-1/2 w-1 h-full -translate-x-1/2 bg-gradient-to-b from-primary-500 via-secondary-500 to-accent-500">
                </div>

                <!-- Timeline Steps -->
                <div class="space-y-16 md:space-y-24">
                    <div v-for="(step, stepIndex) in timelineSteps" :key="stepIndex" class="relative">
                        <!-- Main Step Heading -->
                        <div class="flex justify-center mb-8 md:mb-12">
                            <div
                                class="relative z-10 bg-white px-6 py-3 md:px-8 md:py-4 rounded-tl-xl rounded-br-xl shadow-lg border-2 border-primary-500">
                                <h2 class="text-2xl md:text-3xl font-headings font-bold text-primary-900">{{ step.title }}</h2>
                            </div>
                        </div>

                        <!-- Substeps: single side on mobile, alternating on desktop -->
                        <div v-reveal class="">
                            <div v-for="(substep, substepIndex) in step.substeps" :key="substepIndex"
                                class="relative flex items-stretch flex-row md:items-center"
                                :class="substepIndex % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'">
                                <!-- Icon on Timeline -->
                                <div class="absolute left-4 md:left-1/2 -translate-x-1/2 z-20">
                                    <div class="bg-white rounded-tl-xl rounded-br-xl p-2 md:p-3 shadow-lg border-2"
                                        :class="getIconBorderClass(substep.type)">
                                        <component :is="getIconForType(substep.type)" class="w-5 h-5 md:w-6 md:h-6"
                                            :class="getIconColorClass(substep.type)" />
                                    </div>
                                </div>

                                <!-- Substep Card: full width left of line on mobile, 5/12 on desktop -->
                                <div class="w-full min-w-0 pl-14 md:pl-0 md:w-5/12 group md:pr-4 md:pl-4"
                                    :class="substepIndex % 2 === 0 ? 'md:text-right' : 'md:text-left'">
                                    <div class="block">
                                        <div
                                            class="bg-white rounded-lg shadow-md p-4 border-2 border-transparent hover:border-primary-300 hover:shadow-xl transition-all duration-300 group-hover:-translate-y-1">
                                            <router-link :to="substep.link" class="cursor-pointer flex items-center gap-3 mb-3 justify-start"
                                                :class="substepIndex % 2 === 0 ? 'md:justify-end' : 'md:justify-start'">
                                                <component :is="getIconForType(substep.type)" class="w-5 h-5 shrink-0"
                                                    :class="getIconColorClass(substep.type)" />
                                                <span class="text-xs font-semibold uppercase tracking-wide"
                                                    :class="getTypeColorClass(substep.type)">
                                                    {{ substep.type }}
                                                </span>
                                            </router-link>
                                            <h3
                                                class="text-lg md:text-xl font-headings font-bold text-gray-900 mb-2 group-hover:text-primary-600 transition-colors">
                                                {{ substep.title }}
                                            </h3>
                                            <p class="text-gray-600 text-sm leading-relaxed">
                                                {{ substep.description }}
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                <!-- Spacer for opposite side (desktop only) -->
                                <div class="hidden md:block md:w-5/12"></div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref } from 'vue'
import {
    RocketLaunchIcon,
    UserGroupIcon,
    MapPinIcon,
    DocumentCheckIcon,
    GlobeAltIcon,
    CalendarIcon,
    ChartBarIcon,
    QrCodeIcon,
    VideoCameraIcon,
    ClipboardDocumentCheckIcon,
    BuildingOfficeIcon,
    PresentationChartLineIcon,
    CheckCircleIcon,
    StarIcon,
    ArrowRightIcon,
    BellIcon,
    MegaphoneIcon,
    ClipboardDocumentListIcon
} from '@heroicons/vue/24/outline'

const TimelineStepType = {
    CMS: 'CMS',
    RegistrationTickets: 'Registration & Tickets',
    WebsiteBuilder: 'Website Builder',
    Badges: 'Badges',
    Networking: 'Networking',
    Leads: 'Leads',
    Exhibitors: 'Exhibitors',
    Testing: 'Testing',
    Communication: 'Communication',
    Engagement: 'Engagement',
    Mobile: 'Mobile',
    Navigation: 'Navigation',
    Marketing: 'Marketing',
    CheckIn: 'Check-In',
    Tracking: 'Tracking',
    Analytics: 'Analytics',
    Feedback: 'Feedback',
    Reports: 'Reports',
    Planning: 'Planning'
}

const TYPE_ICON_MAP = {
    [TimelineStepType.CMS]: GlobeAltIcon,
    [TimelineStepType.RegistrationTickets]: CalendarIcon,
    [TimelineStepType.WebsiteBuilder]: ChartBarIcon,
    [TimelineStepType.Badges]: QrCodeIcon,
    [TimelineStepType.Networking]: VideoCameraIcon,
    [TimelineStepType.Leads]: ClipboardDocumentCheckIcon,
    [TimelineStepType.Exhibitors]: BuildingOfficeIcon,
    [TimelineStepType.Testing]: CheckCircleIcon,
    [TimelineStepType.Communication]: BellIcon,
    [TimelineStepType.Engagement]: UserGroupIcon,
    [TimelineStepType.Mobile]: RocketLaunchIcon,
    [TimelineStepType.Navigation]: MapPinIcon,
    [TimelineStepType.Marketing]: MegaphoneIcon,
    [TimelineStepType.CheckIn]: QrCodeIcon,
    [TimelineStepType.Tracking]: ClipboardDocumentListIcon,
    [TimelineStepType.Analytics]: PresentationChartLineIcon,
    [TimelineStepType.Feedback]: StarIcon,
    [TimelineStepType.Reports]: DocumentCheckIcon,
    [TimelineStepType.Planning]: ArrowRightIcon
}

const getIconForType = (type) => TYPE_ICON_MAP[type] ?? ChartBarIcon
const timelineSteps = ref([
    {
        title: 'Setup',
        substeps: [
            {
                title: 'Start your event',
                description: 'Create a new event instantly from a template, a previous event, or from scratch. Add collaborators and activate when you’re ready.',
                type: TimelineStepType.CMS,
                link: '/overview'
            },
            {
                title: 'Apply design and core settings',
                description: 'Configure branding, project settings and enabled features. Turn on only what you need, exactly when you need it.',
                type: TimelineStepType.CMS,
                link: '/overview'
            },
            {
                title: 'Configure profiles and permissions',
                description: 'Define attendee types and control what each role can see and do. Create rich profiles to support logistics, demographics and reporting.',
                type: TimelineStepType.CMS,
                link: '/overview'
            },
            {
                title: 'Build registration forms and tickets',
                description: 'Create single or group booking forms with cascading logic, discount codes and time-based pricing. Fully customise branding and messaging.',
                type: TimelineStepType.RegistrationTickets,
                link: '/features/registrationandtickets'
            },
            {
                title: 'Design and launch your website',
                description: 'Build with flexible content blocks and update as your event evolves. Use your own domain with automated, best-in-class SSL.',
                type: TimelineStepType.WebsiteBuilder,
                link: '/features/websitesbuilder'
            },
            {
                title: 'Launch registrations',
                description: 'Import contacts, send invitations and share your links. Monitor live registrations and revenue, and connect Google Analytics or Meta Pixel for conversion tracking.',
                type: TimelineStepType.RegistrationTickets,
                link: '/features/registrationandtickets'
            },
            {
                title: 'Add gamification and engagement',
                description: 'Enable activity feeds, messaging and Passport competitions. Configure points and achievements to drive the behaviour you want.',
                type: TimelineStepType.Engagement,
                link: '/features/audienceparticipation'
            },
            {
                title: 'Configure exhibitors and sponsors',
                description: 'Create sponsorship tiers and allow self-managed portals. Assign tasks, deadlines and exhibitor-only information.',
                type: TimelineStepType.Exhibitors,
                link: '/features/exhibitorportals'
            },
            {
                title: 'Load company data and grant access',
                description: 'Add exhibiting companies and send access details. Teams can manage staff, products and lead capture settings immediately.',
                type: TimelineStepType.Exhibitors,
                link: '/features/exhibitorportals'
            },
            {
                title: 'Build schedules and speakers',
                description: 'Add sessions, speakers, documents and abstracts. Configure access rules, session selection limits and feedback forms.',
                type: TimelineStepType.CMS,
                link: '/overview'
            },
            {
                title: 'Create meeting blocks',
                description: 'Define meeting time blocks and enable structured networking between attendees and exhibitors.',
                type: TimelineStepType.Networking,
                link: '/features/meetingsandnetworking'
            },
            {
                title: 'Add interactive floorplans',
                description: 'Create simple layouts or fully mapped wayfinding with rooms, booths and points of interest.',
                type: TimelineStepType.Navigation,
                link: '/features/interactivefloorplans'
            },
            {
                title: 'Configure kiosks and badge printing',
                description: 'Design badges with conditional logic and branding. Set up self-service or staff-assisted kiosks ready for event day.',
                type: TimelineStepType.Badges,
                link: '/features/badgesandkiosks'
            },
            {
                title: 'Build additional event content',
                description: 'Use the page builder to add venue details, FAQs, competitions, social events and everything in between.',
                type: TimelineStepType.WebsiteBuilder,
                link: '/features/websitesbuilder'
            },
            {
                title: 'Design menu and navigation',
                description: 'Create a clear, engaging navigation experience. Use conditional logic to tailor visibility by role.',
                type: TimelineStepType.Navigation,
                link: '/overview'
            },
            {
                title: 'Import additional profiles',
                description: 'Create or bulk import staff, VIPs, volunteers or external ticketing data to keep everything in one system.',
                type: TimelineStepType.CMS,
                link: '/overview'
            },
            {
                title: 'Prepare communications',
                description: 'Build and queue email templates and push notifications for welcomes, reminders, updates and sponsored messages.',
                type: TimelineStepType.Communication,
                link: '/features/emailsandcommunications'
            }
        ]
    },
    {
        title: 'Engage',
        substeps: [
            {
                title: 'Go live',
                description: 'Activate the platform, send access details and welcome attendees into the experience.',
                type: TimelineStepType.Mobile,
                link: '/features/eventapp'
            },
            {
                title: 'Open session Q&A and polling',
                description: 'Enable moderated Q&A, convert questions into polls and drive real-time session interaction.',
                type: TimelineStepType.Engagement,
                link: '/features/audienceparticipation'
            },
            {
                title: 'Activate the activity feed',
                description: 'Build momentum before and during the event through live posts, comments and updates.',
                type: TimelineStepType.Engagement,
                link: '/features/audienceparticipation'
            },
            {
                title: 'Enable session selection',
                description: 'Allow attendees to register for sessions within capacity rules and build their personalised schedule.',
                type: TimelineStepType.RegistrationTickets,
                link: '/overview'
            },
            {
                title: 'Send targeted reminders',
                description: 'Use smart filters to prompt incomplete profiles, unfinished tasks or upcoming milestones.',
                type: TimelineStepType.Communication,
                link: '/features/emailsandcommunications'
            }
        ]
    },
    {
        title: 'Onsite',
        substeps: [
            {
                title: 'Activate onsite hardware',
                description: 'Connect and launch badge printers and kiosks directly from the App or CMS.',
                type: TimelineStepType.Badges,
                link: '/features/badgesandkiosks'
            },
            {
                title: 'Enable connections and lead capture',
                description: 'Let attendees and exhibitors scan to connect, qualify leads and manage meetings. All data syncs instantly across the platform.',
                type: TimelineStepType.Leads,
                link: '/features/leadcapture'
            },
            {
                title: 'Drive live engagement',
                description: 'Reward participation with points, achievements and Passport progress. Add new incentives in real time.',
                type: TimelineStepType.Engagement,
                link: '/features/audienceparticipation'
            },
            {
                title: 'Manage attendees on the go',
                description: 'Scan QR codes, resend access details, update profiles and trigger remote badge prints from anywhere.',
                type: TimelineStepType.CheckIn,
                link: '/features/badgesandkiosks'
            },
            {
                title: 'Track session attendance',
                description: 'Use QR self-scanning or staff scanning for live attendance tracking and reporting.',
                type: TimelineStepType.Tracking,
                link: '/features/attendancetracking'
            }
        ]
    },
    {
        title: 'Review',
        substeps: [
            {
                title: 'Review and export data',
                description: 'Filter, analyse and export reports across registrations, engagement, leads and attendance.',
                type: TimelineStepType.Analytics,
                link: '/overview'
            },
            {
                title: 'Send post-event communications',
                description: 'Follow up with attendees, sponsors and exhibitors. Share insights and gather final feedback.',
                type: TimelineStepType.Communication,
                link: '/features/emailsandcommunications'
            },
            {
                title: 'Reuse what worked',
                description: 'Create templates or import configurations into your next event to save time and scale success.',
                type: TimelineStepType.Planning,
                link: '/overview'
            },
            {
                title: 'Celebrate your success',
                description: 'Take a breath - you’ve delivered another exceptional event!',
                type: TimelineStepType.Feedback,
                link: '/overview'
            }
        ]
    }
])


const getIconBorderClass = (type) => {
    const typeMap = {
        'CMS': 'border-primary-500',
        'Website': 'border-primary-500',
        'Website Builder': 'border-primary-500',
        'Registration': 'border-primary-500',
        'Registration & Tickets': 'border-primary-500',
        'Communication': 'border-secondary-500',
        'Content': 'border-secondary-500',
        'Analytics': 'border-accent-500',
        'Badges': 'border-primary-500',
        'Networking': 'border-secondary-500',
        'Leads': 'border-accent-500',
        'Exhibitors': 'border-primary-500',
        'Testing': 'border-secondary-500',
        'Engagement': 'border-accent-500',
        'Mobile': 'border-primary-500',
        'Navigation': 'border-secondary-500',
        'Marketing': 'border-accent-500',
        'Check-In': 'border-primary-500',
        'Tracking': 'border-secondary-500',
        'Feedback': 'border-accent-500',
        'Reports': 'border-primary-500',
        'Planning': 'border-secondary-500'
    }
    return typeMap[type] || 'border-gray-500'
}

const getIconColorClass = (type) => {
    const typeMap = {
        'CMS': 'text-primary-500',
        'Website': 'text-primary-500',
        'Website Builder': 'text-primary-500',
        'Registration': 'text-primary-500',
        'Registration & Tickets': 'text-primary-500',
        'Communication': 'text-secondary-500',
        'Content': 'text-secondary-500',
        'Analytics': 'text-accent-500',
        'Badges': 'text-primary-500',
        'Networking': 'text-secondary-500',
        'Leads': 'text-accent-500',
        'Exhibitors': 'text-primary-500',
        'Testing': 'text-secondary-500',
        'Engagement': 'text-accent-500',
        'Mobile': 'text-primary-500',
        'Navigation': 'text-secondary-500',
        'Marketing': 'text-accent-500',
        'Check-In': 'text-primary-500',
        'Tracking': 'text-secondary-500',
        'Feedback': 'text-accent-500',
        'Reports': 'text-primary-500',
        'Planning': 'text-secondary-500'
    }
    return typeMap[type] || 'text-gray-500'
}

const getTypeColorClass = (type) => {
    const typeMap = {
        'CMS': 'text-primary-600',
        'Website': 'text-primary-600',
        'Website Builder': 'text-primary-600',
        'Registration': 'text-primary-600',
        'Registration & Tickets': 'text-primary-600',
        'Communication': 'text-secondary-600',
        'Content': 'text-secondary-600',
        'Analytics': 'text-accent-600',
        'Badges': 'text-primary-600',
        'Networking': 'text-secondary-600',
        'Leads': 'text-accent-600',
        'Exhibitors': 'text-primary-600',
        'Testing': 'text-secondary-600',
        'Engagement': 'text-accent-600',
        'Mobile': 'text-primary-600',
        'Navigation': 'text-secondary-600',
        'Marketing': 'text-accent-600',
        'Check-In': 'text-primary-600',
        'Tracking': 'text-secondary-600',
        'Feedback': 'text-accent-600',
        'Reports': 'text-primary-600',
        'Planning': 'text-secondary-600'
    }
    return typeMap[type] || 'text-gray-600'
}
</script>

<style scoped>
/* Additional custom styles if needed */
</style>